<template>
  <div class="form-builder">
    <div class="form-builder-header">
      <!-- <h2>Form Builder</h2> -->
       <h2></h2>
      <div class="header-actions">
        <button type="button" class="btn btn-success" @click="handleAddContainer">
          + Add Container
        </button>
        <button type="button" class="btn btn-primary" @click="handleExport">
          Export JSON
        </button>
        <button type="button" class="btn btn-secondary" @click="handleImport">
          Import JSON
        </button>
      </div>
    </div>

    <div class="form-builder-body">
      <div v-if="formStructure.containers.length === 0" class="empty-builder">
        <h4>No containers yet</h4>
        <p class="text-muted">Click "Add Container" to start building your form</p>
      </div>

      <BuilderContainer
        v-for="(container, index) in formStructure.containers"
        :key="container.uid"
        :container="container"
        :container-index="index"
        :selected-uid="selectedUid"
        @select="handleSelect"
        @edit="handleEdit"
        @update="handleContainerUpdate"
        @delete="handleDeleteContainer"
        @copy="handleCopyContainer"
        @copy-column="handleCopyColumn"
        @save-column="handleSaveColumn"
        @reorder="handleReorderContainers"
      />
    </div>

    <EditingModal
      :show="showModal"
      :component="editingComponent"
      @close="handleCloseModal"
      @save="handleSave"
    />

    <div v-if="showJsonModal" class="modal-backdrop fade show" @click="showJsonModal = false"></div>
    <div v-if="showJsonModal" class="modal fade show" style="display: block;" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ jsonModalMode === 'export' ? 'Export' : 'Import' }} Form Structure</h5>
            <button type="button" class="btn-close" @click="showJsonModal = false"></button>
          </div>
          <div class="modal-body">
            <textarea
              class="form-control font-monospace"
              rows="15"
              v-model="jsonData"
              :readonly="jsonModalMode === 'export'"
            ></textarea>
            <div v-if="jsonError" class="alert alert-danger mt-2">{{ jsonError }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showJsonModal = false">
              {{ jsonModalMode === 'export' ? 'Close' : 'Cancel' }}
            </button>
            <button
              v-if="jsonModalMode === 'export'"
              type="button"
              class="btn btn-primary"
              @click="handleCopyJson"
            >
              Copy to Clipboard
            </button>
            <button
              v-if="jsonModalMode === 'import'"
              type="button"
              class="btn btn-primary"
              @click="handleImportJson"
            >
              Import
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { FormStructure, Container, Column, FormComponent } from '../types/form-builder';
import { createContainer, findComponentByUID } from '../utils/form-helpers';
import { saveComponent } from '../utils/storage';
import BuilderContainer from './builder/BuilderContainer.vue';
import EditingModal from './EditingModal.vue';

interface Props {
  initialStructure?: FormStructure;
}

interface Emits {
  (e: 'update', structure: FormStructure): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const formStructure = ref<FormStructure>(
  props.initialStructure || {
    version: '1.0.0',
    containers: [],
    metadata: {
      title: 'Untitled Form',
      createdAt: new Date().toISOString(),
    },
  }
);

const selectedUid = ref<string | undefined>(undefined);
const showModal = ref(false);
const editingComponent = ref<FormComponent | null>(null);
const showJsonModal = ref(false);
const jsonModalMode = ref<'export' | 'import'>('export');
const jsonData = ref('');
const jsonError = ref('');

function handleAddContainer() {
  const newContainer = createContainer();
  formStructure.value.containers.push(newContainer);
  emitUpdate();
}

function handleContainerUpdate(updatedContainer: Container) {
  const index = formStructure.value.containers.findIndex(
    (c) => c.uid === updatedContainer.uid
  );

  if (index !== -1) {
    formStructure.value.containers[index] = updatedContainer;
    emitUpdate();
  }
}

function handleDeleteContainer(uid: string) {
  formStructure.value.containers = formStructure.value.containers.filter(
    (c) => c.uid !== uid
  );
  emitUpdate();
}

function handleCopyContainer(container: Container) {
  const newContainer: Container = {
    ...JSON.parse(JSON.stringify(container)),
    uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };
  regenerateUIDs(newContainer);

  const index = formStructure.value.containers.findIndex(c => c.uid === container.uid);
  formStructure.value.containers.splice(index + 1, 0, newContainer);
  emitUpdate();
}

function handleCopyColumn(column: Column) {
  const newColumn: Column = {
    ...JSON.parse(JSON.stringify(column)),
    uid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };
  regenerateColumnUIDs(newColumn);

  for (const container of formStructure.value.containers) {
    const colIndex = container.columns.findIndex(c => c.uid === column.uid);
    if (colIndex !== -1) {
      container.columns.splice(colIndex + 1, 0, newColumn);
      emitUpdate();
      return;
    }
  }
}

function handleSaveColumn(column: Column) {
  saveComponent(column as any).then(() => {
    alert('Column saved!');
  });
}

function handleReorderContainers(fromIndex: number, toIndex: number) {
  const containers = [...formStructure.value.containers];
  const [moved] = containers.splice(fromIndex, 1);
  containers.splice(toIndex, 0, moved);
  formStructure.value.containers = containers;
  emitUpdate();
}

function regenerateUIDs(container: Container) {
  container.uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  for (const column of container.columns) {
    regenerateColumnUIDs(column);
  }
}

function regenerateColumnUIDs(column: Column) {
  column.uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  for (const element of column.elements) {
    element.uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

function handleSelect(uid: string) {
  selectedUid.value = uid;
}

function handleEdit(uid: string) {
  const result = findComponentByUID(formStructure.value.containers, uid);

  if (result.component) {
    editingComponent.value = result.component;
    showModal.value = true;
  }
}

function handleCloseModal() {
  showModal.value = false;
  editingComponent.value = null;
}

function handleSave(updatedComponent: FormComponent) {
  const result = findComponentByUID(formStructure.value.containers, updatedComponent.uid);

  if (!result.component) return;

  if (updatedComponent.type === 'container') {
    const index = formStructure.value.containers.findIndex(
      (c) => c.uid === updatedComponent.uid
    );
    if (index !== -1) {
      formStructure.value.containers[index] = updatedComponent as Container;
    }
  } else {
    updateNestedComponent(formStructure.value.containers, updatedComponent);
  }

  emitUpdate();
  handleCloseModal();
}

function updateNestedComponent(containers: Container[], updatedComponent: FormComponent) {
  for (const container of containers) {
    for (let i = 0; i < container.columns.length; i++) {
      if (container.columns[i].uid === updatedComponent.uid) {
        container.columns[i] = updatedComponent as any;
        return true;
      }

      if (updateInColumn(container.columns[i], updatedComponent)) {
        return true;
      }
    }
  }
  return false;
}

function updateInColumn(column: any, updatedComponent: FormComponent): boolean {
  for (let i = 0; i < column.elements.length; i++) {
    if (column.elements[i].uid === updatedComponent.uid) {
      column.elements[i] = updatedComponent;
      return true;
    }

    if (column.elements[i].type === 'column') {
      if (updateInColumn(column.elements[i], updatedComponent)) {
        return true;
      }
    }
  }
  return false;
}

function handleExport() {
  jsonData.value = JSON.stringify(formStructure.value, null, 2);
  jsonModalMode.value = 'export';
  jsonError.value = '';
  showJsonModal.value = true;
}

function handleImport() {
  jsonData.value = '';
  jsonModalMode.value = 'import';
  jsonError.value = '';
  showJsonModal.value = true;
}

function handleCopyJson() {
  navigator.clipboard.writeText(jsonData.value).then(() => {
    alert('Copied to clipboard!');
  });
}

function handleImportJson() {
  try {
    const parsed = JSON.parse(jsonData.value);

    if (!parsed.version || !Array.isArray(parsed.containers)) {
      throw new Error('Invalid form structure');
    }

    formStructure.value = parsed;
    emitUpdate();
    showJsonModal.value = false;
    jsonError.value = '';
  } catch (error) {
    jsonError.value = 'Invalid JSON format or structure';
  }
}

function emitUpdate() {
  formStructure.value.metadata = {
    ...formStructure.value.metadata,
    updatedAt: new Date().toISOString(),
  };
  emit('update', formStructure.value);
}
</script>

<style scoped>
.form-builder {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 20px;
}

.form-builder-header {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.form-builder-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.form-builder-body {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 400px;
}

.empty-builder {
  text-align: center;
  padding: 60px 20px;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal {
  z-index: 1050;
}
</style>
